<template>
  <!--
    @todo: remove `is-light`/`is-dark` by leveraging `--chapter-foreground`
    once the colors_css file is updated on AWS.
  -->
  <div 
    class="cm-nav-chapter-dropdown" 
    :class="currentChapter && `ch_${currentChapter.id}`"
    @focusout="handleFocusOut"
  >
    <!-- side effect: nav__toggle adds some styling and opens a modal when clicked on mobile -->
    <button
      class="cm-nav-chapter-dropdown__toggle nav__toggle"
      aria-controls="menu--chapters"
      aria-expanded="false"
    >
      <span class="nav__icon ss-icon">navigate</span>
    </button>
    <div class="cm-nav-chapter-dropdown__container">
      <input
        type="text"
        :placeholder="currentChapter ? currentChapter.name : 'Find your city'"
        class="cm-nav-chapter-dropdown__input is-boxed"
        v-model="textInput"
        ref="input"
        autocomplete="off"
        @focus="isListVisible = true"
        @keydown.up="(e) => onArrow(e, -1)"
        @keydown.down="(e) => onArrow(e, 1)"
        @keydown.enter="onEnter"
        @keydown.esc="onEscape"
        role="combobox"
        aria-haspopup="listbox"
        aria-autocomplete="list"
        :aria-controls="`${uid}-results`"
        aria-label="Find your city"
        spellcheck="false"
        :aria-expanded="isListVisible"
        autocapitalize="off"
        :aria-activedescendant="selectedChapter ? selectedChapter.uid : null"
      />
      <ul class="main-menu nav__mobile-menu mobile" id="menu--chapters">
        <li 
          v-for="chapter in filteredChapters"
          :key="`mobile-${chapter.uid}`"
          class="cm-nav-chapter-dropdown__city"
          :class="`ch_${chapter.id}`"
          role="option"
        >
          <a
            :href="chapter.link"
            :id="chapter.uid"
          >
            <span class="cm-nav-chapter-dropdown__title">{{
              chapter.name
            }}</span>
          </a>
        </li>
      </ul>
      <ul
        class="cm-nav-chapter-dropdown__list desktop"
        :id="`${uid}-results`"
        v-show="isListVisible"
        role="listbox"
      >
        <li 
          v-for="chapter in filteredChapters"
          :key="`desktop-${chapter.uid}`"
          class="cm-nav-chapter-dropdown__city"
          :class="`ch_${chapter.id}`"
          :aria-selected="chapter.abbreviation === selectedChapterAbbr"
          role="option"
        >
          <a
            :href="chapter.link"
            tabindex="-1"
            :id="chapter.uid"
          >
            <span class="cm-nav-chapter-dropdown__title">{{
              chapter.name
            }}</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { v4 as uuid } from "uuid";
export default {
  props: {
    currentChapterAbbr: {
      type: String,
    },
  },

  data() {
    return {
      response: null,
      textInput: "",
      selectedChapterAbbr: null,
      isListVisible: false,
      uid: uuid(),
    };
  },

  created() {
    axios.post("/cities.json?sort_key=closest").then((response) => {
      this.response = response;
    });
  },

  computed: {
    chapters() {
      return (
        (this.response &&
          this.response.data.map((chapter) => ({
            ...chapter,
            link: `/cities/${chapter.abbreviation}`,
            uid: `${this.uid}-chapter-${chapter.abbreviation}`,
          }))) ||
        []
      );
    },
    filteredChapters() {
      return this.chapters.filter((chapter) =>
        chapter.name.toLowerCase().includes(this.textInput.toLowerCase())
      );
    },
    selectedChapter() {
      return this.findChapter(this.selectedChapterAbbr);
    },
    currentChapter() {
      return this.findChapter(this.currentChapterAbbr);
    },
  },

  watch: {
    selectedChapterAbbr(newChapter) {
      this.$nextTick(() => {
        const element = this.$el.querySelector(`[aria-selected="true"]`);
        if (element) {
          element.scrollIntoView({
            block: "nearest",
          });
        }
      });
    },
  },

  methods: {
    findChapter(chapterAbbr) {
      if (!chapterAbbr) {
        return null;
      }
      return this.chapters.find(
        (chapter) =>
          chapter.abbreviation.toLowerCase() === chapterAbbr.toLowerCase()
      );
    },
    onArrow(event, direction) {
      this.isListVisible = true;
      event.preventDefault(); // prevent caret from jumping to start/end

      const index = this.filteredChapters.findIndex(
        (chapter) => chapter.abbreviation === this.selectedChapterAbbr
      );
      let newIndex;

      // navigate to the previous/next chapter in the list
      if (this.selectedChapterAbbr) {
        newIndex =
          (index + direction + this.filteredChapters.length) %
          this.filteredChapters.length;
      } else {
        if (direction === -1) {
          newIndex = this.filteredChapters.length - 1;
        } else {
          newIndex = 0;
        }
      }

      this.selectedChapterAbbr = this.filteredChapters[newIndex].abbreviation;
    },

    onEnter() {
      if (this.filteredChapters.length === 1) {
        window.location = this.filteredChapters[0].link;
      } else if (this.selectedChapterAbbr) {
        window.location = this.findChapter(this.selectedChapterAbbr).link;
      }
    },

    handleFocusOut(e) {
      if (!this.$el.contains(e.relatedTarget)) {
        this.isListVisible = false;
      }
    },

    onEscape() {
      this.isListVisible = false;
      this.selectedChapterAbbr = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.cm-nav-chapter-dropdown {
  width: 100%;
  height: calc(100% * 2 / 3);
  @include desktop-menu() {
    position: absolute;
    left: calc(100% + 4px);
  }
  &__container {
    @include desktop-menu() {
      position: relative;
      height: 100%;
      // disclosure arrow
      &::after {
        @include icon;
        content: "";
        font-size: 10px;
        color: var(--chapter-foreground);
        position: absolute;
        right: 0.6rem;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
      }
    }
  }
  &__toggle {
    margin-left: auto;
    @include desktop-menu() {
      display: none;
    }
  }
  &__input {
    @include mobile-menu() {
      display: none;
    }
    width: 100%;
    height: 100%;
    padding-inline-end: 1.6rem;
    --placeholder-color: var(--chapter-foreground);
    background: var(--chapter-color, var(--bg-2));
    color: var(--box-text-color, var(--fg));
    border-radius: 0;
  }
  #menu--chapters {
    gap: 0;
    padding: 0;
  }
  &__list {
    position: absolute;
    min-width: 100%;
    @include mobile-menu() {
      top: 100px;
      left: 0;
      width: 100%;
    }
    @include desktop-menu() {
      top: calc(100% + 1px);
      max-height: calc(100vh - 8rem);
      overflow: auto;
    }
  }
  &__city {
    background: var(--chapter-color);
    color: var(--chapter-foreground);
    white-space: nowrap;
    &:hover,
    &[aria-selected] {
      text-decoration: underline;
    }
    a {
      display: block;
      padding-inline: 1rem;
      padding-block: 0.5rem;
      @include desktop-menu() {
        padding-inline: 0.5rem;
        padding-block: 0.3rem;
      }
    }
  }
  &__title {
  }
}
</style>



// WEBPACK FOOTER //
// app/javascript/components/cm-nav-chapter-dropdown.vue?9aa3779c